import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { device } from '../../../utils/constants';
import {
  LabBottomRule,
  labAos,
  labBodyMobile,
  labSectionPadMobile,
  labSubtitleMobile,
  labTitleMobile,
} from './labShared';

const base = process.env.PUBLIC_URL || '';
const NATHALIE_IMG = `${base}/img/lab/1.png`;
const NATHALIE_IMG_HOVER = `${base}/img/lab/1.png`;
const IGNACIO_IMG = `${base}/img/lab/3.jpg`;
const VALENTINA_IMG = `${base}/img/lab/2.png`;
const VALENTINA_IMG_HOVER = `${base}/img/lab/2.png`;

const Section = styled.section`
  color: #ffffff;
  font-family: Helvetica, Arial, sans-serif;
  padding-top: 5vw;
  box-sizing: border-box;
  ${labSectionPadMobile}
`;

const SectionTitle = styled.h2`
  margin: 0 auto;
  max-width: min(72rem, 100%);
  text-align: center;
  font-size: 3vw;
  font-family: 'Horizon';
  letter-spacing: 0.12em;
  text-transform: uppercase;
  line-height: 1.2;
  ${labTitleMobile}

  @media ${device.mobile} {
    font-size: 1.5em;
  }
`;

const Row = styled.div`
  margin: 0 auto;
  max-width: min(80rem, 100%);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: clamp(1.5rem, 4vw, 2.5rem);

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
    gap: clamp(2rem, 5vw, 2.5rem);
  }
`;

const MemberBlock = styled.div`
  margin-top: clamp(4rem, 10vw, 6rem);

  &:first-of-type {
    margin-top: clamp(2.5rem, 6vw, 4rem);
  }

  @media ${device.mobile} {
    margin-top: clamp(2.5rem, 10vw, 3.5rem);

    &:first-of-type {
      margin-top: clamp(2rem, 8vw, 3rem);
    }
  }
`;

const TextCol = styled.div`
  text-align: left;
  flex: 1 1 auto;
  min-width: 0;

  ${({ $paddingRight }) =>
    $paddingRight &&
    `
    padding-right: ${$paddingRight};
  `}

  ${({ $paddingLeft }) =>
    $paddingLeft &&
    `
    padding-left: ${$paddingLeft};
  `}

  @media ${device.mobile} {
    padding-left: 0;
    padding-right: 0;
  }
`;

const MemberName = styled.p`
  margin: 0;
  font-size: 2.2vw;
  font-family: 'TelegrafRegular';
  ${labBodyMobile}
`;

const RoleTitle = styled.h3`
  font-family: 'Horizon';
  font-size: 2vw;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  line-height: 1.2;
  ${labSubtitleMobile}
`;

const Bio = styled.p`
  margin-bottom: 3vw;
  margin-top: 3vw;
  font-family: 'TelegrafLight';
  font-size: 1.2vw;
  line-height: 1.3;
  ${labBodyMobile}

  strong {
    font-family: 'TelegrafUltraBold';
  }

  @media ${device.mobile} {
    margin-top: clamp(1rem, 4vw, 1.25rem);
    margin-bottom: clamp(1rem, 4vw, 1.25rem);
  }
`;

const PortraitWrap = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: min(100%, 380px);
  aspect-ratio: 3 / 4;
  overflow: hidden;
  background: transparent;

  ${({ $align }) =>
    $align === 'start'
      ? `
    margin-left: 0;
    margin-right: auto;
  `
      : `
    margin-left: auto;
    margin-right: 0;
  `}

  ${({ $mobileFirst }) =>
    $mobileFirst &&
    `
    @media ${device.mobile} {
      order: -1;
    }
  `}

  @media ${device.mobile} {
    width: min(100%, 280px);
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    align-self: center;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.55s ease;
    pointer-events: none;

    @media (prefers-reduced-motion: reduce) {
      transition-duration: 0.01ms;
    }
  }

  img:first-of-type {
    opacity: 1;
    z-index: 1;
  }

  ${({ $hasHover }) =>
    $hasHover
      ? `
    img:last-of-type {
      opacity: 0;
      z-index: 2;
    }
  `
      : ''}

  @media (hover: hover) and (pointer: fine) {
    ${({ $hasHover }) =>
      $hasHover
        ? `
      &:hover img:first-of-type {
        opacity: 0;
      }

      &:hover img:last-of-type {
        opacity: 1;
      }
    `
        : ''}
  }
`;

const BottomBlock = styled.div`
  padding-top: clamp(3rem, 8vw, 5rem);
  padding-bottom: clamp(1.25rem, 3vw, 2rem);
`;

const TheTeamLab = () => {
  const { t } = useTranslation();

  return (
    <Section aria-labelledby="lab-the-team-heading">
      <SectionTitle id="lab-the-team-heading" {...labAos('fade-up')}>
        {t('lab.theTeam.title')}
      </SectionTitle>

      <MemberBlock {...labAos('fade-up', 80)}>
        <Row>
          <TextCol $paddingRight="4vw" {...labAos('fade-up', 120)}>
            <MemberName>{t('lab.theTeam.nathalie.name')}</MemberName>
            <RoleTitle>{t('lab.theTeam.nathalie.role')}</RoleTitle>

            <Bio>{t('lab.theTeam.nathalie.bio1')}</Bio>
            <Bio
              dangerouslySetInnerHTML={{
                __html: t('lab.theTeam.nathalie.bio1Black'),
              }}
            />
            <Bio>{t('lab.theTeam.nathalie.bio2')}</Bio>
          </TextCol>

          <PortraitWrap
            $align="end"
            $hasHover
            $mobileFirst
            {...labAos('fade-up', 200)}
          >
            <img src={NATHALIE_IMG} alt="" decoding="async" />
            <img src={NATHALIE_IMG_HOVER} alt="" decoding="async" />
          </PortraitWrap>
        </Row>
      </MemberBlock>

      <MemberBlock {...labAos('fade-up', 80)}>
        <Row>
          <PortraitWrap
            $align="start"
            $hasHover={false}
            {...labAos('fade-up', 120)}
          >
            <img src={IGNACIO_IMG} alt="" decoding="async" />
          </PortraitWrap>

          <TextCol $paddingLeft="4vw" {...labAos('fade-up', 200)}>
            <MemberName>{t('lab.theTeam.ignacio.name')}</MemberName>
            <RoleTitle>{t('lab.theTeam.ignacio.role')}</RoleTitle>

            <Bio>{t('lab.theTeam.ignacio.bio1')}</Bio>
            <Bio
              dangerouslySetInnerHTML={{
                __html: t('lab.theTeam.ignacio.bio1Black'),
              }}
            />
            <Bio>{t('lab.theTeam.ignacio.bio2')}</Bio>
          </TextCol>
        </Row>
      </MemberBlock>

      <MemberBlock {...labAos('fade-up', 80)}>
        <Row>
          <TextCol $paddingRight="4vw" {...labAos('fade-up', 120)}>
            <MemberName>{t('lab.theTeam.valentina.name')}</MemberName>
            <RoleTitle
              dangerouslySetInnerHTML={{
                __html: t('lab.theTeam.valentina.role'),
              }}
            />

            <Bio
              dangerouslySetInnerHTML={{
                __html: t('lab.theTeam.valentina.bio1'),
              }}
            />
            <Bio
              dangerouslySetInnerHTML={{
                __html: t('lab.theTeam.valentina.bio1Black'),
              }}
            />
          </TextCol>

          <PortraitWrap
            $align="end"
            $hasHover
            $mobileFirst
            {...labAos('fade-up', 200)}
          >
            <img src={VALENTINA_IMG} alt="" decoding="async" />
            <img src={VALENTINA_IMG_HOVER} alt="" decoding="async" />
          </PortraitWrap>
        </Row>
      </MemberBlock>

      <BottomBlock>
        <LabBottomRule aria-hidden />
      </BottomBlock>
    </Section>
  );
};

export default TheTeamLab;
